import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

const productLinks = [
  { label: 'Protective Electronic Cases', href: '/products#protective', desc: 'Rugged protection for sensitive electronics' },
  { label: 'Rackmount Cases', href: '/products#rackmount', desc: 'Double-entry rackmount transport solutions' },
  { label: 'Shock Mount Deck Cases', href: '/products#shock-mount', desc: 'Vibration-isolated mounting systems' },
  { label: 'Custom Foam Inserts', href: '/design', desc: 'Design your own foam layout online' },
  // KAN-COMP-PRODESIGN-001: paid expert-design service tier
  { label: 'Pro Design Service', href: '/pro-design', desc: 'Our engineers build your foam layout for you' },
];

const industryLinks = [
  { label: 'Aerospace', href: '/industries#aerospace' },
  { label: 'Medical', href: '/industries#medical' },
  { label: 'Military / Mil-Spec', href: '/industries#military' },
  { label: 'Government', href: '/industries#government' },
  { label: 'Telecommunications', href: '/industries#telecom' },
  { label: 'Industrial', href: '/industries#industrial' },
  { label: 'Video Equipment', href: '/industries#video' },
];

export default function Header() {
  const location = useLocation();
  const { user, isAuthenticated } = useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownTimeout = useRef<ReturnType<typeof setTimeout>>();
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const handleDropdownEnter = (name: string) => {
    clearTimeout(dropdownTimeout.current);
    setOpenDropdown(name);
  };

  const handleDropdownLeave = () => {
    dropdownTimeout.current = setTimeout(() => setOpenDropdown(null), 200);
  };

  const isActive = (path: string) => location.pathname === path;
  const isActivePrefix = (prefix: string) => location.pathname.startsWith(prefix);

  return (
    <header
      ref={headerRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-slate-900/95 backdrop-blur-md shadow-lg shadow-black/20'
          : 'bg-slate-900'
      }`}
    >
      {/* Top utility bar */}
      <div className={`border-b border-slate-800 transition-all duration-300 ${scrolled ? 'h-0 overflow-hidden opacity-0' : 'h-auto opacity-100'}`}>
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between text-xs text-slate-400">
          <div className="flex items-center gap-4">
            <a href="tel:3033244953" className="flex items-center gap-1.5 hover:text-white transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
              (303) 324-4953
            </a>
            <a href="mailto:sales@foamcasebuilder.com" className="flex items-center gap-1.5 hover:text-white transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              sales@foamcasebuilder.com
            </a>
          </div>
          <span className="hidden sm:block text-slate-500">Boulder, CO | Advanced Container Solutions</span>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="max-w-7xl mx-auto px-6">
        <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? 'py-2' : 'py-4'}`}>
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <div className={`bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center transition-all duration-300 ${scrolled ? 'w-8 h-8' : 'w-10 h-10'}`}>
              <svg className={`text-white transition-all duration-300 ${scrolled ? 'w-5 h-5' : 'w-6 h-6'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <div className="flex flex-col leading-tight">
              <span className={`font-bold text-white transition-all duration-300 ${scrolled ? 'text-lg' : 'text-xl'}`}>Foam CaseBuilder</span>
              <span className={`text-[10px] tracking-wider uppercase text-slate-400 transition-all duration-300 ${scrolled ? 'opacity-0 h-0' : 'opacity-100'}`}>
                Foam CaseBuilder
              </span>
            </div>
          </Link>

          {/* Desktop nav links */}
          {/* #328: desktop nav reveals at `xl` (1280px), not `lg` (1024px). The full
              link set needs ~1340px to fit, so `lg:flex` left an unhandled 1024–1339px
              band where the right-side CTA + Sign In clipped off-screen with no
              hamburger fallback. Below `xl` the complete mobile drawer is the reachable
              menu; the reduced padding/gap below lets the desktop row fit from 1280px. */}
          <div className="hidden xl:flex items-center gap-0.5">
            <Link
              to="/"
              className={`px-2.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/') ? 'text-blue-400' : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              Home
            </Link>

            {/* Products dropdown */}
            <div
              className="relative"
              onMouseEnter={() => handleDropdownEnter('products')}
              onMouseLeave={handleDropdownLeave}
            >
              <Link
                to="/products"
                className={`px-2.5 py-2 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-1 ${
                  isActivePrefix('/products') ? 'text-blue-400' : 'text-slate-300 hover:text-white hover:bg-slate-800'
                }`}
              >
                Products
                <svg className={`w-3.5 h-3.5 transition-transform ${openDropdown === 'products' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </Link>
              {openDropdown === 'products' && (
                <div className="absolute top-full left-0 pt-2 w-80">
                  <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-2xl shadow-black/40 p-2">
                    {productLinks.map((item) => (
                      <Link
                        key={item.label}
                        to={item.href}
                        className="flex flex-col gap-0.5 px-4 py-3 rounded-lg hover:bg-slate-700 transition-colors"
                      >
                        <span className="text-sm font-medium text-white">{item.label}</span>
                        <span className="text-xs text-slate-400">{item.desc}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Industries dropdown */}
            <div
              className="relative"
              onMouseEnter={() => handleDropdownEnter('industries')}
              onMouseLeave={handleDropdownLeave}
            >
              <Link
                to="/industries"
                className={`px-2.5 py-2 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-1 ${
                  isActivePrefix('/industries') ? 'text-blue-400' : 'text-slate-300 hover:text-white hover:bg-slate-800'
                }`}
              >
                Industries
                <svg className={`w-3.5 h-3.5 transition-transform ${openDropdown === 'industries' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </Link>
              {openDropdown === 'industries' && (
                <div className="absolute top-full left-0 pt-2 w-64">
                  <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-2xl shadow-black/40 p-2">
                    {industryLinks.map((item) => (
                      <Link
                        key={item.label}
                        to={item.href}
                        className="block px-4 py-2.5 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-slate-700 transition-colors"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`px-2.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/about') ? 'text-blue-400' : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              About
            </Link>

            <Link
              to="/foam-guide"
              className={`px-2.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/foam-guide') ? 'text-blue-400' : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              Foam guide
            </Link>

            <Link
              to="/faq"
              className={`px-2.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/faq') ? 'text-blue-400' : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              FAQ
            </Link>

            <Link
              to="/tutorial"
              className={`px-2.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/tutorial') ? 'text-blue-400' : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              Tutorial
            </Link>

            <Link
              to="/gallery"
              className={`px-2.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/gallery') ? 'text-blue-400' : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              Gallery
            </Link>

            <Link
              to="/premade-cases"
              className={`px-2.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/premade-cases') ? 'text-blue-400' : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              Premade Cases
            </Link>

            <Link
              to="/compare"
              className={`px-2.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/compare') ? 'text-blue-400' : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              Compare
            </Link>

            <Link
              to="/blog"
              className={`px-2.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActivePrefix('/blog') ? 'text-blue-400' : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              Blog
            </Link>

            <Link
              to="/contact"
              className={`px-2.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive('/contact') ? 'text-blue-400' : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              Contact
            </Link>

            {isAuthenticated && (
              <>
                <Link
                  to="/designs"
                  className={`px-2.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive('/designs') ? 'text-blue-400' : 'text-slate-300 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  My Designs
                </Link>
                <Link
                  to="/profile"
                  className={`px-2.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive('/profile') ? 'text-blue-400' : 'text-slate-300 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  Profile
                </Link>
              </>
            )}
          </div>

          {/* Right side: CTA + auth */}
          <div className="hidden xl:flex items-center gap-2">
            <Link
              to="/design"
              className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-all shadow-lg shadow-blue-600/20 hover:shadow-blue-500/30"
            >
              Start Designing
            </Link>

            {isAuthenticated ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 max-w-[120px] truncate">{user?.email}</span>
                <Link to="/profile" className="text-slate-400 hover:text-white transition-colors" title="Your profile" aria-label="Your profile">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                </Link>
              </div>
            ) : (
              <Link to="/login" className="text-sm text-slate-400 hover:text-white transition-colors font-medium">
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="xl:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            ) : (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 xl:hidden" onClick={() => setMobileOpen(false)} />
          <div className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-slate-900 border-l border-slate-700 z-50 xl:hidden overflow-y-auto">
            <div className="p-6 space-y-1">
              <div className="flex items-center justify-between mb-6">
                <span className="font-bold text-white text-lg">Menu</span>
                <button onClick={() => setMobileOpen(false)} aria-label="Close menu" className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              <Link to="/" className="block px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 font-medium">Home</Link>
              <Link to="/products" className="block px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 font-medium">Products</Link>
              <Link to="/pro-design" className="block px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 font-medium">Pro Design</Link>
              <Link to="/industries" className="block px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 font-medium">Industries</Link>
              <Link to="/gallery" className="block px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 font-medium">Gallery</Link>
              <Link to="/premade-cases" className="block px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 font-medium">Premade Cases</Link>
              <Link to="/compare" className="block px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 font-medium">Compare</Link>
              <Link to="/blog" className="block px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 font-medium">Blog</Link>
              <Link to="/shop" className="block px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 font-medium">Shop</Link>
              <Link to="/about" className="block px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 font-medium">About</Link>
              <Link to="/foam-guide" className="block px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 font-medium">Foam guide</Link>
              <Link to="/faq" className="block px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 font-medium">FAQ</Link>
              <Link to="/tutorial" className="block px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 font-medium">Tutorial</Link>
              <Link to="/contact" className="block px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 font-medium">Contact</Link>

              {isAuthenticated && (
                <>
                  <Link to="/designs" className="block px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 font-medium">My Designs</Link>
                  <Link to="/profile" className="block px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 font-medium">Profile</Link>
                </>
              )}

              <div className="pt-4 space-y-3">
                <Link to="/design" className="block w-full text-center bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold px-5 py-3 rounded-lg">
                  Start Designing
                </Link>
                {!isAuthenticated && (
                  <Link to="/login" className="block w-full text-center border border-slate-600 text-slate-300 font-medium px-5 py-3 rounded-lg hover:bg-slate-800">
                    Sign In
                  </Link>
                )}
              </div>

              <div className="pt-6 border-t border-slate-700 mt-4 space-y-2 text-sm text-slate-400">
                <a href="tel:3033244953" className="flex items-center gap-2 hover:text-white">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                  (303) 324-4953
                </a>
                <a href="mailto:sales@foamcasebuilder.com" className="flex items-center gap-2 hover:text-white">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  sales@foamcasebuilder.com
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
}
